import { useState } from "react";
import { Download, FileDown, Mail, Share2 } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { useT } from "../i18n";

const W = 1080, H = 1920, ORANGE = "#FF5A1F", BLACK = "#0A0A0A", OFF = "#F5F5F2", MUTED = "#8A8A86", PANEL = "#161616";

const wrap = (ctx, text, maxWidth) => {
  const words = text.split(" "), lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; } else line = test;
  }
  if (line) lines.push(line);
  return lines;
};

export async function renderPlanCard(plan, t = (k) => k) {
  await document.fonts.load("80px Anton").catch(() => {});
  await document.fonts.load("500 30px Inter").catch(() => {});
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = BLACK; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = ORANGE; ctx.fillRect(0, 0, W, 14);
  ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";

  ctx.font = "56px Anton"; ctx.fillStyle = OFF;
  let x = 72; ctx.fillText("RO", x, 120); x += ctx.measureText("RO").width;
  ctx.fillStyle = ORANGE; ctx.fillText("X", x, 120); x += ctx.measureText("X").width;
  ctx.fillStyle = OFF; ctx.fillText("PAIR", x, 120);
  ctx.font = "600 24px Inter"; ctx.fillStyle = ORANGE; ctx.fillText(t("plan_brand").toUpperCase() + " · " + plan.race_name.toUpperCase(), 72, 175);

  ctx.font = "104px Anton"; ctx.fillStyle = OFF; ctx.fillText(t("img_sessions"), 72, 290);
  ctx.fillStyle = ORANGE; ctx.fillText(t("img_together"), 72, 395);
  ctx.font = "500 30px Inter"; ctx.fillStyle = OFF;
  ctx.fillText(`${plan.me.first_name.toUpperCase()} + ${plan.partner.first_name.toUpperCase()} · ${t("img_common_time")} ${Math.floor(plan.target_time / 60)}h${String(plan.target_time % 60).padStart(2, "0")}`, 72, 450);

  let y = 520;
  plan.sessions.forEach((s, i) => {
    ctx.fillStyle = PANEL; ctx.fillRect(72, y, W - 144, 236);
    ctx.fillStyle = ORANGE; ctx.fillRect(72, y, 10, 236);
    ctx.font = "44px Anton"; ctx.fillStyle = ORANGE; ctx.fillText(`${i + 1}`, 110, y + 62);
    ctx.fillStyle = OFF;
    const title = wrap(ctx, s.title.toUpperCase(), W - 144 - 120)[0];
    ctx.fillText(title, 160, y + 62);
    ctx.font = "500 26px Inter"; ctx.fillStyle = MUTED;
    wrap(ctx, `${s.focus} · ${s.duration}`, W - 144 - 110).slice(0, 2).forEach((l, k) => ctx.fillText(l, 110, y + 108 + k * 34));
    ctx.font = "500 26px Inter"; ctx.fillStyle = OFF;
    s.blocks.slice(1, 3).forEach((b, k) => {
      const l = wrap(ctx, "— " + b, W - 144 - 110)[0];
      ctx.fillText(l.length < ("— " + b).length ? l.replace(/\s\S*$/, "…") : l, 110, y + 190 + k * 36);
    });
    y += 254;
  });

  ctx.font = "56px Anton"; ctx.fillStyle = OFF; ctx.fillText(t("img_split_a"), 72, y + 50);
  ctx.fillStyle = ORANGE; ctx.fillText(t("img_split_b"), 72 + ctx.measureText(t("img_split_a")).width, y + 50);
  y += 80;
  ctx.font = "600 24px Inter";
  const colW = (W - 144 - 40) / 2;
  plan.split.forEach((s, i) => {
    const cx = 72 + (i % 2) * (colW + 40), cy = y + Math.floor(i / 2) * 52;
    ctx.fillStyle = MUTED; ctx.textAlign = "left"; ctx.fillText(s.station.toUpperCase(), cx, cy + 30);
    ctx.textAlign = "right";
    const share = `${s.share} %`, lead = `${s.lead.toUpperCase()} `;
    ctx.fillStyle = ORANGE; ctx.fillText(share, cx + colW, cy + 30);
    ctx.fillStyle = OFF; ctx.fillText(lead, cx + colW - ctx.measureText(share).width - 6, cy + 30);
    ctx.fillStyle = "#2A2A2A"; ctx.fillRect(cx, cy + 44, colW, 2);
  });
  ctx.textAlign = "center"; ctx.font = "44px Anton"; ctx.fillStyle = OFF;
  ctx.fillText(t("img_tag"), W / 2, H - 45);
  ctx.fillStyle = ORANGE; ctx.fillRect(0, H - 14, W, 14);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export const PlanShare = ({ plan }) => {
  const { t } = useT();
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  const download = async () => {
    setBusy("image"); setMsg(null);
    try {
      const blob = await renderPlanCard(plan, t);
      const file = new File([blob], `roxpair-plan-${plan.partner.first_name.toLowerCase()}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: `ROXPAIR — ${t("plan_brand")}` }).catch(() => {});
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
      }
      setMsg({ ok: true, text: t("plan_image_done") });
    } finally { setBusy(""); }
  };

  const sendMail = async () => {
    setBusy("mail"); setMsg(null);
    try {
      const { data } = await api.post(`/matches/${plan.match_id}/plan/send`);
      setMsg({ ok: true, text: t("plan_sent_to", plan.partner.first_name, data.to) });
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e) });
    } finally { setBusy(""); }
  };

  return (
    <div className="mt-8 space-y-3 print:hidden" data-testid="plan-share">
      <button data-testid="plan-print" className="btn-primary" onClick={() => window.print()}>
        <FileDown size={18} /> {t("plan_pdf")}
      </button>
      <button data-testid="plan-send-email" className="btn-ghost" onClick={sendMail} disabled={!!busy}>
        <Mail size={18} /> {busy === "mail" ? t("plan_sending") : t("plan_send_email", plan.partner.first_name)}
      </button>
      <button data-testid="plan-download" className="btn-ghost" onClick={download} disabled={!!busy}>
        {navigator.canShare ? <Share2 size={18} /> : <Download size={18} />} {busy === "image" ? "…" : t("plan_download_image")}
      </button>
      {msg && <p data-testid="plan-share-message" className={`text-sm ${msg.ok ? "text-rox-off/80" : "text-rox-orange"}`}>{msg.text}</p>}
    </div>
  );
};
